import type {
  ApiConfig,
  AuthResponse,
  User,
  VoiceType,
  ScriptStyle,
  TaskSubmission,
  TaskListResponse,
  TaskStatusResponse,
  TaskSubmissionRequest,
  TaskAttachment,
  SynthesizeRequest,
  SynthesizeAdvancedRequest,
  SynthesizeResponse,
  GenerateScriptRequest,
  ScriptResponse,
  GenerateTitleRequest,
  TitleResponse,
  GenerateImageRequest,
  GenerateImageResponse,
  ImageTaskStatusResponse,
  DocumentExtractionResponse,
} from '../types/api';

class ApiClient {
  private baseURL: string;
  private token: string | null;
  private timeout: number;

  constructor(config?: ApiConfig) {
    this.baseURL = config?.baseURL || '/api';
    this.token = localStorage.getItem('token');
    this.timeout = config?.timeout || 180000;
  }

  getToken(): string | null {
    return this.token;
  }

  setToken(token: string | null): void {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  private getHeaders(includeAuth = true): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (includeAuth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorMessage = await this.extractErrorMessage(response);
      throw new Error(errorMessage);
    }
    return this.parseResponse<T>(response);
  }

  private async extractErrorMessage(response: Response): Promise<string> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') return errorData.detail;
          if (errorData.detail.message) return errorData.detail.message;
          if (errorData.detail.error) return errorData.detail.error;
        }
        return errorData.message || `HTTP ${response.status}`;
      }
    } catch {
      // fall through
    }
    return `HTTP ${response.status}: ${response.statusText}`;
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      return {} as T;
    } catch {
      throw new Error('响应数据解析失败');
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const controller = new AbortController();
    const config: RequestInit = {
      headers: this.getHeaders(),
      ...options,
      signal: controller.signal,
    };

    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, config);
      return await this.handleResponse<T>(response);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') throw new Error('请求超时');
        if (error.message.includes('fetch')) throw new Error('网络连接失败，请检查网络设置');
        throw error;
      }
      throw new Error('未知错误');
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : null,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async upload<T>(
    endpoint: string,
    file: File,
    options: {
      originalFilename?: string;
      onProgress?: (progress: number) => void;
      formFields?: Record<string, string>;
    } = {}
  ): Promise<T> {
    const { originalFilename, onProgress, formFields } = options;
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${this.baseURL}${endpoint}`;
      const formData = new FormData();
      formData.append('file', file);
      if (originalFilename) {
        formData.append('original_filename', originalFilename);
      }
      if (formFields) {
        Object.entries(formFields).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        });
      }
      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            reject(new Error('响应数据解析失败'));
          }
        } else {
          reject(new Error(`上传失败: ${xhr.status}`));
        }
      });
      xhr.addEventListener('error', () => reject(new Error('网络错误，上传失败')));
      xhr.addEventListener('timeout', () => reject(new Error('上传超时')));
      xhr.open('POST', url);
      xhr.timeout = this.timeout;
      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }
      xhr.send(formData);
    });
  }

  // Auth
  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${this.baseURL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await this.handleResponse<AuthResponse>(response);
    this.setToken(data.access_token);
    return data;
  }

  logout(): void {
    this.setToken(null);
  }

  async getCurrentUser(): Promise<User> {
    return this.get<User>('/auth/me');
  }

  async verifyToken(): Promise<{ valid: boolean; user: User }> {
    return this.post('/auth/verify-token');
  }

  // Scripts
  async getScriptStyles(): Promise<{ styles: ScriptStyle[]; total: number }> {
    const response = await this.get<{
      success?: boolean;
      styles?: Record<string, ScriptStyle> | ScriptStyle[];
      total?: number;
    }>('/script-styles');
    const payload = response.styles || [];
    const normalized: ScriptStyle[] = Array.isArray(payload)
      ? payload
      : Object.values(payload);
    return { styles: normalized, total: response.total ?? normalized.length };
  }

  async generateScript(request: GenerateScriptRequest): Promise<ScriptResponse> {
    return this.post<ScriptResponse>('/scripts', request);
  }

  async generateTitle(request: GenerateTitleRequest): Promise<TitleResponse> {
    return this.post<TitleResponse>('/scripts/title', request);
  }

  // TTS
  async getVoiceTypes(): Promise<{ voice_types: VoiceType[] }> {
    return this.get('/voice-types');
  }

  async uploadDocument(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<DocumentExtractionResponse> {
    return this.upload<DocumentExtractionResponse>('/upload', file, {
      onProgress,
    });
  }

  async synthesize(request: SynthesizeRequest): Promise<SynthesizeResponse> {
    return this.post<SynthesizeResponse>('/speech', request);
  }

  async synthesizeAdvanced(request: SynthesizeAdvancedRequest): Promise<SynthesizeResponse> {
    return this.post<SynthesizeResponse>('/speech/advanced', request);
  }

  async downloadAttachment(attachmentId: number): Promise<Blob> {
    const url = `${this.baseURL}/task-attachments/${attachmentId}/download`;
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });
    if (!response.ok) {
      const errorMessage = await this.extractErrorMessage(response);
      throw new Error(errorMessage);
    }
    return response.blob();
  }

  // Tasks
  async getTaskStatus(taskId: string): Promise<TaskStatusResponse> {
    return this.get<TaskStatusResponse>(`/tasks/${taskId}`);
  }

  async uploadTaskAttachment(
    file: File,
    originalFilename: string,
    onProgress?: (progress: number) => void
  ): Promise<TaskAttachment> {
    return this.upload<TaskAttachment>('/task-attachments', file, {
      originalFilename,
      onProgress,
    });
  }

  async createTaskSubmission(payload: TaskSubmissionRequest): Promise<TaskSubmission> {
    return this.post<TaskSubmission>('/task-submissions', payload);
  }

  async getTaskSubmissions(): Promise<TaskListResponse> {
    return this.get<TaskListResponse>('/task-submissions');
  }

  async getTaskSubmission(submissionId: number): Promise<TaskSubmission> {
    return this.get<TaskSubmission>(`/task-submissions/${submissionId}`);
  }

  async retryTask(submissionId: number): Promise<TaskSubmission> {
    return this.post<TaskSubmission>(`/task-submissions/${submissionId}/retry`);
  }

  // Images
  async generateImage(request: GenerateImageRequest): Promise<GenerateImageResponse> {
    return this.post<GenerateImageResponse>('/images/generate', request);
  }

  async getImageTaskStatus(taskId: string): Promise<ImageTaskStatusResponse> {
    return this.get<ImageTaskStatusResponse>(`/images/tasks/${taskId}`);
  }

  // Storage
  async refreshSignedUrl(
    url: string,
    expires: number = 86400
  ): Promise<{ signed_url: string; expires_in: number; storage_type: string }> {
    const params = new URLSearchParams({ url, expires: expires.toString() });
    return this.get(`/storage/refresh-url?${params}`);
  }

  // Health
  async checkConnection(): Promise<boolean> {
    try {
      await this.get('/health');
      return true;
    } catch {
      return false;
    }
  }
}

export const api = new ApiClient();
export default api;